import React from 'react';
import _ from 'lodash';
import { OpeningHoursTypes } from '../types';

interface TimeRange {
  openTime: string;
  closeTime: string;
}

interface OpeningHours {
  id: string;
  open: boolean;
  timeRanges: TimeRange[];
}

interface CombinedOpeningHours {
  id: string;
  open: boolean;
  openTime?: string;
  closeTime?: string;
}

const formatOpenHours = (openingHours: OpeningHoursTypes[] | null) => {
  if (!openingHours) return null;

  const dayOrder: string[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  // Create a copy of the array before sorting (to avoid mutating read-only arrays)
  const sortedHours: OpeningHours[] = [...openingHours].sort(
    (a, b) => dayOrder.indexOf(a.id) - dayOrder.indexOf(b.id),
  );

  // Helper function to compare if two opening hour objects have identical schedules
  function haveSameSchedule(a: OpeningHours, b: OpeningHours): boolean {
    return _.isEqual(
      { open: a.open, timeRanges: a.timeRanges },
      { open: b.open, timeRanges: b.timeRanges },
    );
  }

  // Helper function to convert 24-hour time to 12-hour AM/PM format
  function convertTo12Hour(time24: string): string {
    const [hours, minutes] = time24.split(':');
    const hour: number = parseInt(hours, 10);
    const minute: string = minutes;

    if (hour === 0) {
      return `12:${minute}am`;
    }
    if (hour < 12) {
      return `${hour}:${minute}am`;
    }
    if (hour === 12) {
      return `12:${minute}pm`;
    }
    return `${hour - 12}:${minute}pm`;
  }

  // Helper function to create day range label
  function createDayLabel(days: string[]): string {
    if (days.length === 1) {
      // Single day - use full name
      return days[0].charAt(0).toUpperCase() + days[0].slice(1);
    }

    // Check if days are consecutive
    const dayIndices: number[] = days.map((day) => dayOrder.indexOf(day));
    const isConsecutive: boolean = dayIndices.every(
      (index, i) => i === 0 || index === dayIndices[i - 1] + 1,
    );

    if (isConsecutive) {
      // Multiple consecutive days - use abbreviated names
      const dayAbbreviations: Record<string, string> = {
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
        sunday: 'Sun',
      };

      const firstDay: string = dayAbbreviations[days[0]];
      const lastDay: string = dayAbbreviations[days[days.length - 1]];
      return `${firstDay}-${lastDay}`;
    }
    // If not consecutive, join with commas using full names
    return days.map((day) => day.charAt(0).toUpperCase() + day.slice(1)).join(', ');
  }

  // Use reduce to group consecutive days with same schedule
  const groups: OpeningHours[][] = sortedHours.reduce(
    (acc: OpeningHours[][], current: OpeningHours, index: number) => {
      if (index === 0 || !haveSameSchedule(current, sortedHours[index - 1])) {
        // Start new group
        acc.push([current]);
      } else {
        // Add to current group
        acc[acc.length - 1].push(current);
      }
      return acc;
    },
    [],
  );

  // Transform groups into combined objects
  return groups.map((group: OpeningHours[]): CombinedOpeningHours => {
    const dayIds: string[] = group.map((item) => item.id);
    const firstTimeRange = group[0].timeRanges[0];

    const result: CombinedOpeningHours = {
      id: createDayLabel(dayIds),
      open: group[0].open,
    };

    // Add openTime and closeTime only if the place is open and has time ranges
    if (group[0].open && firstTimeRange) {
      result.openTime = convertTo12Hour(firstTimeRange.openTime);
      result.closeTime = convertTo12Hour(firstTimeRange.closeTime);
    }

    return result;
  });
};

export default formatOpenHours;
